import React, { useEffect, useState } from "react";
import axios from "../../utility/customAxios";
import { MetoroChart, ChartType } from "../Charts/MetoroChart";
import { MetricToChartData } from "../../pages/MetricsTest";
import { Metric } from "../../clients/metoro/metrics";
import { MultiSelectorDropDown, DropDownItem, stringArrayToDropDownItemArray } from "../Input/MultiSelectorDropdown/MultiSelectorDropDown";
import { SingleSelectDropdown } from "../Input/SingleSelectDropdown";
import {useDispatch, useSelector} from "react-redux";
import { TimeRange } from "../../types/time";
import timerange, {set} from "../../store/reducers/timerange";
import {useSearchParams} from "react-router-dom";

interface DataUsageTabProps {
    className?: string;
}

interface DataSentRequest {
    startTime: number;
    endTime: number;
    environments?: string[];
    usageTypes?: string[];
    bucketSize?: number;
    splits?: string[];
}

interface GetMetricResponse {
    metric: Metric;
    isResultLimited: boolean;
    resultLen: number;
}

const USAGE_TYPES = [
    { value: "logs", displayName: "Logs" },
    { value: "custom_metrics", displayName: "Custom Metrics" },
    { value: "prometheus_remote_write", displayName: "Prometheus Remote Write" },
    { value: "traces", displayName: "Traces" },
    { value: "profiling", displayName: "Profiling" },
    { value: "k8s_update_batch", displayName: "K8s Update Batch" },
    { value: "k8s_snapshot", displayName: "K8s Snapshot" },
    { value: "unknown", displayName: "Unknown" }
];

const SPLIT_OPTIONS = [
    { value: "environment", displayName: "Environment" },
    { value: "usageType", displayName: "Usage Type" }
];

const BUCKET_SIZE_OPTIONS = [
    { value: "3600", displayName: "1 Hour" },
    { value: "86400", displayName: "1 Day" },
    { value: "604800", displayName: "1 Week" }
];

export function DataUsageTab({ className }: DataUsageTabProps) {
    const timeRange = useSelector(timerange.selectors.getTimeRange)
    const dispatch = useDispatch();
    const [dataUsage, setDataUsage] = useState<Metric>();
    const [loading, setLoading] = useState(false);
    const [environments, setEnvironments] = useState<DropDownItem[]>([]);
    const [selectedEnvironments, setSelectedEnvironments] = useState<DropDownItem[]>([]);
    const [selectedUsageTypes, setSelectedUsageTypes] = useState<DropDownItem[]>([]);
    const [selectedSplits, setSelectedSplits] = useState<DropDownItem[]>([]);
    const [selectedBucketSize, setSelectedBucketSize] = useState<DropDownItem>({ value: "86400", displayName: "1 Day" }); // Default to 1 day

    useEffect(() => {
        dispatch(set(new TimeRange("30d")))
    }, []);

    // Fetch available environments
    useEffect(() => {
        axios.get("/api/v1/environments").then((response) => {
            const sortedEnvironments = [...response.data.environments].sort((a, b) => a.localeCompare(b));
            setEnvironments(stringArrayToDropDownItemArray(sortedEnvironments));
        }).catch((e) => {
            console.error("Failed to fetch environments:", e);
        });
    }, []);

    // Fetch data usage
    const fetchDataUsage = async () => {
        setLoading(true);
        try {
            // Use time range from Redux store
            const startTime = timeRange.getStartEnd()[0].getTime();
            const endTime = timeRange.getStartEnd()[1].getTime();

            const request: DataSentRequest = {
                startTime,
                endTime,
                bucketSize: parseInt(selectedBucketSize.value),
                environments: selectedEnvironments.length > 0 ? selectedEnvironments.map(e => e.value) : undefined,
                usageTypes: selectedUsageTypes.length > 0 ? selectedUsageTypes.map(u => u.value) : undefined,
                splits: selectedSplits.length > 0 ? selectedSplits.map(s => s.value) : undefined
            };

            const response = await axios.post<GetMetricResponse>('/api/v1/billing/getDataSent', request);
            setDataUsage(response.data.metric);
        } catch (error) {
            console.error("Failed to fetch data usage:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data on component mount and when filters or time range change
    useEffect(() => {
        fetchDataUsage();
    }, [selectedEnvironments, selectedUsageTypes, selectedSplits, selectedBucketSize, timeRange]);

    const getTimeUnit = () => {
        switch (selectedBucketSize.value) {
            case "3600": return "hour";
            case "604800": return "week";
            default: return "day";
        }
    };

    const getTimeUnitDisplay = () => {
        switch (selectedBucketSize.value) {
            case "3600": return "MMM dd HH:mm";
            case "604800": return "MMM dd";
            default: return "dd/MM";
        }
    };

    return (
        <div className={`flex flex-col gap-6 ${className}`}>
            <div className="text-center">
                <h2 className="text-2xl font-semibold text-textlight mb-2">Data Usage</h2>
                <p className="text-textmedium">Monitor your data usage across different environments and types</p>
            </div>
            
            <div className="flex flex-wrap gap-4 px-2">
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-textmedium">Environments</label>
                    <MultiSelectorDropDown
                        selectorSingular="Environment"
                        allSelector="All Environments"
                        possibleItems={environments}
                        selectedItems={selectedEnvironments}
                        setSelectedItems={setSelectedEnvironments}
                        visualSettings={{
                            dropdownWidth: "200px"
                        }}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-textmedium">Usage Types</label>
                    <MultiSelectorDropDown
                        selectorSingular="Usage Type"
                        allSelector="All Usage Types"
                        possibleItems={USAGE_TYPES}
                        selectedItems={selectedUsageTypes}
                        setSelectedItems={setSelectedUsageTypes}
                        visualSettings={{
                            dropdownWidth: "200px"
                        }}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-textmedium">Group By</label>
                    <MultiSelectorDropDown
                        selectorSingular="Split"
                        possibleItems={SPLIT_OPTIONS}
                        selectedItems={selectedSplits}
                        setSelectedItems={setSelectedSplits}
                        visualSettings={{
                            dropdownWidth: "150px"
                        }}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-textmedium">Time Granularity</label>
                    <SingleSelectDropdown
                        displaySettings={{ dontUseSecondaryBackground: true }}
                        selectedItemTitle="Time Granularity"
                        possibleItems={BUCKET_SIZE_OPTIONS}
                        selectedItem={selectedBucketSize}
                        setSelectedItem={setSelectedBucketSize}
                    />
                </div>
            </div>

            <div className="relative flex-none h-[400px] w-full mb-4 flex px-2">
                <MetoroChart
                    className="h-full"
                    title="Data Ingested - Uncompressed"
                    dataToUse={MetricToChartData(dataUsage, undefined, undefined)}
                    type={ChartType.Bar}
                    timeUnit={getTimeUnit()}
                    timeUnitDisplay={getTimeUnitDisplay()}
                    metricUnit={"By"}
                />
            </div>
        </div>
    );
}